import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { Box, Link, Tooltip } from '@mui/material';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useSelector } from '../../../../../redux/store';
import PdfPopupButton from '../../../../../reusable-components/pdf-popup/PdfPopupButton';
import { useTabs } from '../../../../../context/TabContext';
import { InvoicingPath } from '../../../../../utils/globalEnums';
import { fMonthDayYearShort } from '../../../../../utils/formatTime';
import AlertAccordion from '../../../../../reusable-components/accordion-wrapper/AlertAccordion';
import { useDeleteAndNavigate } from '../reusable/hooks/useDeleteAndNavigate';
import { AwaitButton } from '../../../../../reusable-components/await-button';
import { useIsInvoiceEditable } from '../reusable/hooks/useInvoiceLocks';

export const DuplicateInvoiceAlert = () => {
  const { id } = useParams();
  const {
    data: {
      duplicateInvoices: { invoiceId: duplicatesForId, duplicates = [] }
    },
  } = useSelector((state) => state.invoicing);
    const { isEditable, message } = useIsInvoiceEditable();
  const deleteAndNavigate = useDeleteAndNavigate();
  const { addNewTabAndNavigate } = useTabs();
  const [deleteInvoiceInProgress, setDeleteInvoiceInProgress] = useState(false);

  const handleDeleteInvoice = useCallback(async () => {
    try {
      setDeleteInvoiceInProgress(true);
      await deleteAndNavigate();
    } finally {
      setDeleteInvoiceInProgress(false);
    }
  }, [deleteAndNavigate]);

  if (duplicates.length === 0 || duplicatesForId !== Number(id)) return null;
  const allPending = duplicates.every((i) => InvoicingPath[i.path] !== 'previous');
  return (
    <>
      <Box
        sx={{
          width: '92%',
          margin: 'auto',
          pb: '16px',
        }}
      >
        <AlertAccordion
          title={`Duplicate Invoice Detected (${duplicates.length})`}
          color={allPending ? 'warning' : 'error'}
          icon={<ErrorOutlineIcon fontSize="small" color={allPending ? 'warning' : 'error'} />}
          defaultExpanded
        >
          <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
              {duplicates.map((invoice, index) => (
                <Box key={invoice.id} sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {InvoicingPath[invoice.path] === 'previous' ? (
                    <Tooltip title="Invoice Processed">
                      <CheckCircleOutlineOutlinedIcon sx={{ fontSize: '14px', color: '#3688D7' }} />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Invoice in Pending">
                      <PendingOutlinedIcon sx={{ fontSize: '14px', color: '#3688D7' }} />
                    </Tooltip>
                  )}
                  <Link
                    onClick={() => {
                      addNewTabAndNavigate(`/dashboard/invoicing/${InvoicingPath[invoice.path]}/${invoice.id}`);
                    }}
                    sx={{
                      color: '#3688D7',
                      fontSize: '11px',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    {invoice.vendorInvoiceId} - {fMonthDayYearShort(invoice.date)}
                  </Link>
                  <Box sx={{ ml: 1 }}>
                    {invoice.hasMainFile ? (
                      <PdfPopupButton
                        icon={<PictureAsPdfOutlinedIcon />}
                        url={`/Api/Invoices/ViewInvoiceFile?id=${invoice.id}&mainFile=true`}
                        tooltipText={
                          <iframe
                            title={`Invoice PDF ${invoice.id}`}
                            src={`/Api/Invoices/ViewInvoiceFile?id=${invoice.id}&mainFile=true#toolbar=0`}
                            style={{ width: '100%', height: '100%' }}
                          />
                        }
                        largeTooltip
                        row={invoice}
                      />
                    ) : (
                      <Tooltip title="No Invoice PDF available">
                        <PictureAsPdfOutlinedIcon color="disabled" sx={{ fontSize: 18 }} />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 0.75 }}>
        <Tooltip title={!isEditable ? message : ''} placement="top" arrow>
                <span>
                  <AwaitButton
                    variant="outlined"
                    loading={deleteInvoiceInProgress}
                    disabled={!isEditable}
                    onClick={handleDeleteInvoice}
                    color={allPending ? 'warning' : 'error'}
                    size="small"
                    sx={{
                      fontSize: '10px',
                      color: allPending ? 'warning.main' : 'error.main',
                      borderColor: allPending ? 'warning.main' : 'error.main',
                    }}
                  >
                    Delete Current Invoice
                  </AwaitButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </AlertAccordion>
      </Box>
    </>
  );
};
